/**
 * VariantsTabContent — Variants tab for the UnifiedBottomPanel.
 *
 * 2-column grid of variant previews matching the main image aspect ratio,
 * newest-first ordering, version labels (v1, v2...), lit-up accent border
 * on selected, pending spinners for in-flight generations, empty state.
 *
 * Dynamic height: 1–2 variants = 1 row, 3–4 = 2 rows, 5+ = 2.5× capped with scroll.
 *
 * Sprint 9h: initial implementation
 * Sprint 10e: aspect-ratio thumbnails + dynamic height
 */

import { memo } from 'react'
import { cn } from '../../../../lib/utils'
import { getThumbnailUrl } from '../../../../api/useAssetUrl'
import type { HistoryEntry } from './types'
import { ACCENT_COLORS, type AccentColor } from './BaseNode'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VariantsTabContentProps {
  /** Full history array (original order: oldest-first) */
  history: HistoryEntry[]
  /** Currently picked variant index (in original array order) */
  pickedIndex: number
  /** Selection callback (receives original array index) */
  onSelectVariant: (index: number) => void
  /** Number of in-flight pending generations */
  batchPending: number
  /** Cache-bust key for thumbnail URLs */
  cacheKey?: string
  /** Project ID for thumbnail URL construction */
  projectId?: string
  /** Accent color for the selected variant border */
  accentColor?: AccentColor
  /** CSS aspect-ratio for variant thumbnails (e.g., "16/9", "4/3", "1/1"). Defaults to "1/1". */
  aspectRatio?: string
  /** Max width in px for each variant thumbnail to match main image width */
  maxWidth?: number
}

// ---------------------------------------------------------------------------
// Height helper
// ---------------------------------------------------------------------------

/**
 * Parse a CSS aspect-ratio string (e.g., "16/9") into a numeric value (width / height).
 * Returns 1 for invalid/missing input.
 */
function parseAspectRatio(ar: string): number {
  const parts = ar.split('/')
  const w = Number(parts[0])
  const h = Number(parts[1])
  if (!w || !h || w <= 0 || h <= 0) return 1
  return w / h
}

/**
 * Compute the content height for the Variants tab based on variant count and aspect ratio.
 * Exported so consumers can pass this as `contentHeight` to UnifiedBottomPanel.
 *
 * @param variantCount - Total variants (history.length + batchPending)
 * @param aspectRatio - CSS aspect-ratio string (e.g., "16/9"). Defaults to "1/1".
 * @param nodeWidth - Node card width in px. Panel is calc(100% + 120px) wider.
 * @returns Height in pixels
 */
export function getVariantsContentHeight(
  variantCount: number,
  aspectRatio: string = '1/1',
  nodeWidth: number = 400,
): number {
  if (variantCount === 0) return 150

  const arValue = parseAspectRatio(aspectRatio) // width/height
  // Panel width = max(nodeWidth + 120, 632); p-3 = 24px padding, gap-2 = 8px
  const panelWidth = Math.max(nodeWidth + 120, 632)
  const cellWidth = (panelWidth - 24 - 8) / 2
  const cellHeight = cellWidth / arValue
  const gap = 8
  const padding = 24

  const rows = Math.ceil(variantCount / 2)

  if (rows <= 2) {
    return Math.round(rows * cellHeight + (rows - 1) * gap + padding)
  }

  // 5+ variants: cap at 2.5 rows
  return Math.round(2.5 * cellHeight + 1.5 * gap + padding)
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const VariantsTabContent = memo(function VariantsTabContent({
  history,
  pickedIndex,
  onSelectVariant,
  batchPending,
  cacheKey,
  projectId,
  accentColor,
  aspectRatio = '1/1',
  maxWidth,
}: VariantsTabContentProps) {
  const variantCount = history.length + batchPending
  const accent = accentColor ? ACCENT_COLORS[accentColor] : null

  if (variantCount === 0) {
    return (
      <div
        className="h-full flex flex-col items-center justify-center text-center"
        data-testid="variants-empty-state"
      >
        <svg
          className="w-8 h-8 text-text-muted/30 mb-2"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>
        <p className="text-xs text-text-muted">No variants yet</p>
        <p className="text-xxs text-text-muted/60 mt-1">
          Generate to create variants
        </p>
      </div>
    )
  }

  return (
    <div
      className="h-full p-3 overflow-y-auto nowheel"
      data-testid="variants-tab-content"
    >
      <div className="grid grid-cols-2 gap-2">
        {/* Pending spinners (newest-first → appear first) */}
        {Array.from({ length: batchPending }).map((_, i) => (
          <div
            key={`pending-${i}`}
            className="rounded-lg bg-surface-2 flex items-center justify-center border border-border-subtle"
            style={{ aspectRatio, ...(maxWidth ? { maxWidth } : {}) }}
            data-testid="variant-pending"
          >
            <div className="w-5 h-5 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin" />
          </div>
        ))}

        {/* History items (newest first) */}
        {[...history].reverse().map((entry, displayIdx) => {
          const realIdx = history.length - 1 - displayIdx
          const isSelected = realIdx === pickedIndex
          const imagePath = entry.result?.local_path
          const thumbUrl =
            imagePath && projectId
              ? getThumbnailUrl(projectId, imagePath, 200, cacheKey)
              : null

          return (
            <button
              key={entry.result?.id || realIdx}
              onClick={() => onSelectVariant(realIdx)}
              className={cn(
                'relative rounded-lg overflow-hidden transition-all',
                'border-2',
                isSelected
                  ? ''
                  : 'border-transparent hover:border-border'
              )}
              style={{
                ...(maxWidth ? { maxWidth } : {}),
                ...(isSelected && accent
                  ? { borderColor: accent.text }
                  : isSelected
                    ? { borderColor: '#a78bfa' }
                    : undefined),
              }}
              data-testid={`variant-item-${realIdx}`}
            >
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt={`v${realIdx + 1}`}
                  className="w-full object-cover rounded-md"
                  style={{ aspectRatio }}
                />
              ) : (
                <div
                  className="w-full bg-surface-2 flex items-center justify-center rounded-md"
                  style={{ aspectRatio }}
                >
                  <svg
                    className="w-5 h-5 text-text-muted/30"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16"
                    />
                  </svg>
                </div>
              )}

              {/* Version label pill — bottom-left */}
              <div className="absolute bottom-1.5 left-1.5 text-xxs text-white bg-black/60 px-1.5 py-0.5 rounded">
                v{realIdx + 1}
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
})

VariantsTabContent.displayName = 'VariantsTabContent'

export { VariantsTabContent }
